import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getCardInfo, getCardStats } from '@/api/card';
import { getShippingAddresses, createShippingAddress } from '@/api/address';
import { getPaymentMethods, getPaymentCards, getVeritransToken } from '@/api/payment';
import { previewPurchase, createPaymentOrder, getPurchaseIntentStatus } from '@/api/purchase';
import { VERITRANS_TOKEN_API_KEY } from '@/config/constants';
import { getRelatedCards } from '@/api/products';
import { getMyProfile } from '@/api/profile';
import AddressSection from '@/sections/purchase/AddressSection';
import PaymentSection from '@/sections/purchase/PaymentSection';
import OrderSection from '@/sections/purchase/OrderSection';
import OrderSuccessSection from '@/sections/purchase/OrderSuccessSection';
import BindPhoneDialog from '@/components/BindPhoneDialog';
import ConfirmDialog from '@/components/ConfirmDialog';
import FullScreenLoading from '@/components/FullScreenLoading';
import { TooltipProvider } from '@/components/Tooltip';
import { useTranslation } from 'react-i18next';
import { trackEvent } from '@/utils/googleAnalytics';
import PenaltyRestrictionDialog from '@/components/PenaltyRestrictionDialog';
import usePenaltyGuard from '@/hooks/usePenaltyGuard';
import { USER_STATUS } from '@/utils/userStatus';

const PurchasePage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const handleRestrictionSecondary = useCallback(() => {
    navigate('/');
  }, [navigate]);
  const {
    ensureActionAllowed,
    penaltyDialogProps,
    status: penaltyStatus,
    isPenaltyStatusFetched,
    isPenaltyStatusLoading,
  } = usePenaltyGuard({ onSecondaryAction: handleRestrictionSecondary });
  
  // Extract URL parameters
  const psaGrade = searchParams.get('psa');
  const condition = searchParams.get('condition');
  const bundledQuantity = searchParams.get('bundledQuantity') ? parseInt(searchParams.get('bundledQuantity'), 10) : null;
  const price = searchParams.get('price') ? parseInt(searchParams.get('price'), 10) : 0;
  const listingId = searchParams.get('listing');
  const psaListingId = searchParams.get('psa_listing_id');
  const isOfficial = searchParams.get('isOfficial');
  
  const [requirePayment, setRequirePayment] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [orderPreviewData, setOrderPreviewData] = useState(null);
  const [orderCompleted, setOrderCompleted] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [showPhoneDialog, setShowPhoneDialog] = useState(false);
  const [isOutOfStockDialogOpen, setIsOutOfStockDialogOpen] = useState(false);

  // Add state to track editing modes
  const [isAddressEditing, setIsAddressEditing] = useState(false);
  const [isPaymentEditing, setIsPaymentEditing] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [selectedPaymentCardId, setSelectedPaymentCardId] = useState(null);

  // State for confirmation dialog
  const [showEditConfirmDialog, setShowEditConfirmDialog] = useState(false);
  const [pendingEditAction, setPendingEditAction] = useState(null);

  const [paymentRedirectUrl, setPaymentRedirectUrl] = useState(null);
  const [isCheckingPaymentStatus, setIsCheckingPaymentStatus] = useState(false);

  // Ref to track if preview has been called
  const previewCalledRef = useRef(false);
  const restrictionHandledRef = useRef(false);
  
  // Store URL parameters for use in the component
  const purchaseDetails = {
    psaGrade,
    condition,
    bundledQuantity,
    price,
    listingId,
    psaListingId,
    isOfficial
  };

  const { data: addresses, isLoading: isLoadingAddresses } = useQuery({
    queryKey: ['shipping-addresses'],
    queryFn: getShippingAddresses,
  });

  const { data: paymentMethods } = useQuery({
    queryKey: ['payment-methods'],
    queryFn: getPaymentMethods,
  });

  const { data: paymentCards, isLoading: isLoadingPaymentCards } = useQuery({
    queryKey: ['payment-cards'],
    queryFn: getPaymentCards,
  });

  // Fetch user profile to check if phone is bound
  const { data: userProfile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ['user-profile'],
    queryFn: getMyProfile,
  });

  // Auto-select default or first address if addresses exist
  useEffect(() => {
    const selectedAddress = addresses && addresses.data.find(a => a.id == selectedAddressId);
    if (!selectedAddress && addresses?.data?.length > 0) {
      const defaultAddress = addresses.data.find(a => a.isDefault) || addresses.data[0];
      setSelectedAddressId(defaultAddress.id);
    }
  }, [addresses]);

  // Auto-select default or first payment card if cards exist
  useEffect(() => {
    const selectedCard = paymentCards && paymentCards.data.find(c => c.id == selectedPaymentCardId);
    if (!selectedCard && paymentCards?.data?.length > 0) {
      const defaultCard = paymentCards.data.find(c => c.isDefault) || paymentCards.data[0];
      setSelectedPaymentCardId(defaultCard.id);
    }
  }, [paymentCards]);

  useEffect(() => {
    if (!isPenaltyStatusFetched || isPenaltyStatusLoading) {
      return;
    }

    if (penaltyStatus === USER_STATUS.NORMAL) {
      restrictionHandledRef.current = false;
      return;
    }

    if (restrictionHandledRef.current) {
      return;
    }

    restrictionHandledRef.current = true;
    ensureActionAllowed();
  }, [ensureActionAllowed, isPenaltyStatusFetched, isPenaltyStatusLoading, penaltyStatus]);

  useEffect(() => {
    if (selectedAddressId && !paymentData) {
      setRequirePayment(true);
    }
  }, [paymentData, selectedAddressId]);

  // Check for payment status when returning from payment redirect
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const intentId = urlParams.get('intent_id');

    if (intentId && !isCheckingPaymentStatus) {
      // User returned from payment, check status
      checkPaymentStatus(intentId);
    }
  }, []);

  const createAddressMutation = useMutation({
    mutationFn: createShippingAddress,
    onSuccess: (data) => {
      queryClient.invalidateQueries(['shipping-addresses']);
      setSelectedAddressId(data.data.id);
    },
    onError: (error) => {
      console.error('Failed to create address:', error);
    }
  });

  const orderPreviewMutation = useMutation({
    mutationFn: previewPurchase, // Use new API
    onSuccess: (data) => {
      setOrderPreviewData(data);
    },
    onError: (error) => {
      console.error('Failed to fetch order preview:', error);
      setOrderPreviewData(null);
      setIsProcessingPayment(false);

      // Check if error is due to out-of-stock
      if (error?.response?.data?.code === 'out_of_stock') {
        setIsOutOfStockDialogOpen(true);
      }
    }
  });

  const createPaymentOrderMutation = useMutation({
    mutationFn: createPaymentOrder,
    onSuccess: (data) => {
      if (data.data.payment?.redirectUri) {
        // Open payment verification in new window/tab
        const redirectUrl = data.data.payment.redirectUri;
        window.open(redirectUrl, '_blank');

        setPaymentRedirectUrl(redirectUrl);
        // Show full screen loading and start checking payment status
        setIsProcessingPayment(false); // Hide button loading
        setIsCheckingPaymentStatus(true); // Show full screen loading
        checkPaymentStatus(data.data.id);
      } else {
        // Start checking payment status immediately
        checkPaymentStatus(data.data.id);
      }
    },
    onError: (error) => {
      console.error('Failed to create payment order:', error);
      setIsProcessingPayment(false);
      // Check if error is due to out-of-stock
      if (error?.response?.data?.code === 'out_of_stock') {
        setIsOutOfStockDialogOpen(true);
      }
    }
  });

  // Function to check payment status
  const checkPaymentStatus = async (intentId) => {
    setIsCheckingPaymentStatus(true);

    try {
      const statusData = await getPurchaseIntentStatus(intentId);
      const status = statusData.data.status;

      if (status === 'COMPLETED') {
        // Payment successful - show success
        setOrderData(statusData.data);

        trackEvent('user_paid', {
          amount: orderPreviewData?.data?.monetary?.total || 0,
        });

        setOrderCompleted(true);
        setIsProcessingPayment(false);
        setIsCheckingPaymentStatus(false);
      } else if (status === 'FAILED' || status === 'EXPIRED') {
        // Payment failed - show error dialog and go back
        setIsProcessingPayment(false);
        setIsCheckingPaymentStatus(false);
        // Show confirm dialog and navigate back
        setIsOutOfStockDialogOpen(true);
      } else if (status === 'PAYMENT_PROCESSING') {
        // Still processing - check again after delay
        setTimeout(() => checkPaymentStatus(intentId), 2000);
      }
    } catch (error) {
      console.error('Failed to check payment status:', error);
      setIsProcessingPayment(false);
      setIsCheckingPaymentStatus(false);
    }
  };

  const fetchOrderPreview = useCallback(() => {
    // Determine type based on whether it's a PSA graded card, a listing card, or a bundled quantity
    let type = 'CARD_BOX';

    if (purchaseDetails.psaListingId) {
      type = 'PSA_SINGLE_CARD_SPECIFIC';
    } else if (purchaseDetails.psaGrade) {
      type = 'PSA_SINGLE_CARD';
    } else if (purchaseDetails.listingId) {
      type = 'NON_PSA_SINGLE_CARD';
    }

    const previewPayload = {
      type,
      itemId: id,
      price: purchaseDetails.price || 0
    };

    // Add PSA listing ID, PSA grade, listingId, or bundled quantity conditionally
    if (purchaseDetails.psaListingId) {
      previewPayload.listingId = purchaseDetails.psaListingId;
      previewPayload.psa = purchaseDetails.psaGrade;
    } else if (purchaseDetails.psaGrade) {
      previewPayload.psa = purchaseDetails.psaGrade;
    } else if (purchaseDetails.listingId) {
      previewPayload.listingId = purchaseDetails.listingId;
    } else if (purchaseDetails.bundledQuantity) {
      previewPayload.bundledQuantity = purchaseDetails.bundledQuantity;
    }

    previewPayload.isOfficial = purchaseDetails.isOfficial === 'true';
    orderPreviewMutation.mutate(previewPayload);
  }, [id, purchaseDetails, orderPreviewMutation]);

  // Call preview API only once when component loads
  useEffect(() => {
    if (id && !previewCalledRef.current) {
      previewCalledRef.current = true;
      fetchOrderPreview();
    }
  }, [id, fetchOrderPreview]);

  const handleCreateOrder = async () => {
    // Check if user has phone bound
    if (!userProfile?.data?.phone) {
      setShowPhoneDialog(true);
      return;
    }

    // Check if payment data is available
    if (!paymentData) {
      console.error('No payment data available');
      return;
    }

    if (!ensureActionAllowed()) {
      return;
    }

    setIsProcessingPayment(true);

    try {
      // Determine type based on whether it's a PSA graded card, a listing card, or a bundled quantity
      let type = 'CARD_BOX';

      if (purchaseDetails.psaListingId) {
        type = 'PSA_SINGLE_CARD_SPECIFIC';
      } else if (purchaseDetails.psaGrade) {
        type = 'PSA_SINGLE_CARD';
      } else if (purchaseDetails.listingId) {
        type = 'NON_PSA_SINGLE_CARD';
      }

      const orderPayload = {
        type,
        itemId: id,
        price: purchaseDetails.price || 0,
        shippingAddressId: selectedAddressId
      };

      // Add PSA listing ID, PSA grade, listingId, or bundled quantity conditionally
      if (purchaseDetails.psaListingId) {
        orderPayload.listingId = purchaseDetails.psaListingId;
        orderPayload.psa = purchaseDetails.psaGrade;
      } else if (purchaseDetails.psaGrade) {
        orderPayload.psa = purchaseDetails.psaGrade;
      } else if (purchaseDetails.listingId) {
        orderPayload.listingId = purchaseDetails.listingId;
      } else if (purchaseDetails.bundledQuantity) {
        orderPayload.bundledQuantity = purchaseDetails.bundledQuantity;
      }

      orderPayload.isOfficial = purchaseDetails.isOfficial === 'true';

      // Add payment information with new structure
      const redirectUri = `${window.location.origin}/payment/done`;

      if (paymentData.type === 'TEMPORARY_CREDIT_CARD') {
        // New card - get Veritrans token first
        const tokenData = await getVeritransToken({
          cardNumber: paymentData.cardNumber.replace(/\s/g, ''), // Remove spaces
          cardExpire: paymentData.expiry, // Keep MM/YY format like "08/28"
          securityCode: paymentData.cvc,
          cardholderName: paymentData.cardHolder,
          tokenApiKey: VERITRANS_TOKEN_API_KEY,
          lang: 'ja'
        });

        orderPayload.payment = {
          method: {
            type: 'TEMPORARY_CREDIT_CARD',
            token: tokenData.token
          },
          redirectUri: redirectUri
        };
      } else if (paymentData.type === 'SAVED_CREDIT_CARD') {
        // Saved card - just pass card ID
        orderPayload.payment = {
          method: {
            type: 'SAVED_CREDIT_CARD',
            cardId: paymentData.cardId
          },
          redirectUri: redirectUri
        };
      }

      // Call the preview API again to check availability
      const previewPayload = { ...orderPayload };
      delete previewPayload.shippingAddressId;
      delete previewPayload.payment;

      await orderPreviewMutation.mutateAsync(previewPayload);

      // If we get here without an error, proceed with creating the payment order
      createPaymentOrderMutation.mutate(orderPayload);

    } catch (error) {
      console.error('Payment processing error:', error);
      setIsProcessingPayment(false);
      // Handle payment error here - you might want to show an error message to the user
    }
  };

  const { data: cardInfo, isLoading: isLoadingCard } = useQuery({
    queryKey: ['card', id],
    queryFn: () => getCardInfo(id),
  });

  const { data: cardStats, isLoading: isLoadingStats } = useQuery({
    queryKey: ['cardStats', id],
    queryFn: () => getCardStats(id),
  });

  // Preload back image for purchase finish animation
  useEffect(() => {
    if (cardInfo?.data) {
      const backImageUrl = cardInfo.data.backImage || '/card_back.png';
      const img = new Image();
      img.src = backImageUrl;
    }
  }, [cardInfo?.data]);
  
  // Fetch related cards using the new API
  const { data: relatedCardsData } = useQuery({
    queryKey: ['relatedCards', id],
    queryFn: () => getRelatedCards(id),
    enabled: !!id,
  });
  
  // Process related cards - no need to filter
  const relatedCards = relatedCardsData?.data || [];

  const handlePhoneDialogClose = () => {
    setShowPhoneDialog(false);
    // Refresh user profile after phone binding
    queryClient.invalidateQueries(['user-profile']);
  };

  const handleOutOfStockDialogClose = () => {
    setIsOutOfStockDialogOpen(false);
    // Navigate back to the card detail page or another appropriate page
    if (listingId) {
      navigate(`/listing/${listingId}`);
    } else {
      navigate(`/card/${id}`);
    }
  };

  if (isLoadingCard || isLoadingStats || isLoadingAddresses || isLoadingProfile) {
    return (
      <div className='container mx-auto flex justify-center items-center'>
        <span>Loading...</span>
      </div>
    );
  }

  if (!cardInfo?.data) {
    return <div>Card not found</div>;
  }

  // If order is completed, show the success section
  if (orderCompleted && orderData) {
    // only for testing
    orderData.image = cardInfo.data.images[0].url;
    orderData.backImage = cardInfo.data.backImage || '/card_back.png';
    
    return <OrderSuccessSection orderData={orderData} relatedCards={relatedCards} />;
  }

  const handleAddressComplete = (data) => {
    if (data?.addressId) {
      // If we received just an address ID, use the existing address
      setSelectedAddressId(data.addressId);
    } else {
      // Transform the form data to match the API request format
      const addressPayload = {
        firstname: data.firstName,
        lastname: data.lastName,
        firstnameKana: data.firstNameKana,
        lastnameKana: data.lastNameKana,
        contactNumber: data.phone,
        postalCode: data.postalCode,
        prefecture: data.prefecture,
        city: data.city,
        address1: data.street,
        address2: data.building || '',
        isDefault: false
      };

      createAddressMutation.mutate(addressPayload);
    }
  };

  const handleAddressEditingChange = (isEditing) => {
    setIsAddressEditing(isEditing);
  };

  const handlePaymentComplete = (data) => {
    setPaymentData(data);
  };

  const handlePaymentEditingChange = (isEditing) => {
    setIsPaymentEditing(isEditing);
  };

  // Handle edit requests with confirmation if other section is editing
  const handleAddressEditRequest = () => {
    if (isPaymentEditing) {
      setShowEditConfirmDialog(true);
      setPendingEditAction({ type: 'address', message: t('purchase.confirm_dialogs.complete_payment_edit') });
      return false; // Prevent edit
    }
    return true; // Allow edit
  };

  const handlePaymentEditRequest = () => {
    if (isAddressEditing) {
      setShowEditConfirmDialog(true);
      setPendingEditAction({ type: 'payment', message: t('purchase.confirm_dialogs.complete_address_edit') });
      return false; // Prevent edit
    }
    return true; // Allow edit
  };

  const handleEditConfirmDialogClose = () => {
    setShowEditConfirmDialog(false);
    setPendingEditAction(null);
  };

  // Determine if payment section should be active
  const hasPaymentMethods = paymentData !== null;
  
  // Determine if pay button should be active
  const canPay = selectedAddressId &&
                  paymentData &&
                  !isAddressEditing &&
                  !isPaymentEditing &&
                  !isProcessingPayment;

  if (isPenaltyStatusFetched && penaltyStatus !== USER_STATUS.NORMAL) {
    return (
      <>
        <div className="min-h-screen w-full bg-[#f7f7f7]" />
        <PenaltyRestrictionDialog {...penaltyDialogProps} />
      </>
    );
  }

  return (
    <TooltipProvider>
      <div className="min-h-screen w-full bg-[#f7f7f7]">
        {/* Main Content */}
        <div className="container py-6 flex gap-6">
          {/* Left Column */}
          <div className="flex-1 flex flex-col gap-6">
            <AddressSection
              onComplete={handleAddressComplete}
              onEditingChange={handleAddressEditingChange}
              onEditRequest={handleAddressEditRequest}
              addresses={addresses?.data || []}
              finalSelectedAddressId={selectedAddressId}
            />
            <PaymentSection
              onComplete={handlePaymentComplete}
              onEditingChange={handlePaymentEditingChange}
              onEditRequest={handlePaymentEditRequest}
              isRequired={requirePayment}
              hasPaymentMethods={hasPaymentMethods}
              paymentCards={paymentCards?.data || []}
              finalSelectedCardId={selectedPaymentCardId}
            />
            <div className="p-6 bg-white rounded">
              <h3 className="font-bold text-sm mb-2">{t('purchase.about_returns.title')}</h3>

              <h4 className="font-bold text-sm mb-1">{t('purchase.about_returns.cancellations.title')}</h4>
              <p className="text-xs text-[#aaaaaa] mb-2">
                {t('purchase.about_returns.cancellations.credit_line')}
              </p>
              <p className="text-xs text-[#aaaaaa] mb-4">
                {t('purchase.about_returns.cancellations.convenience')}
              </p>

              <h4 className="font-bold text-sm mb-1">{t('purchase.about_returns.returns.title')}</h4>
              <p className="text-xs text-[#aaaaaa] mb-1">
                {t('purchase.about_returns.returns.personal')}
              </p>
              <p className="text-xs text-[#aaaaaa]">
                {t('purchase.about_returns.returns.defective')}
              </p>
            </div>
          </div>

          {/* Right Column */}
          <OrderSection
            card={cardInfo.data}
            stats={cardStats?.data}
            purchaseDetails={purchaseDetails}
            canPay={canPay}
            previewData={orderPreviewData?.data}
            onPayClick={() => handleCreateOrder()}
            isProcessingPayment={isProcessingPayment || isCheckingPaymentStatus}
          />
        </div>

        {/* Phone Binding Dialog */}
        {showPhoneDialog && (
          <BindPhoneDialog onClose={handlePhoneDialogClose} />
        )}

        {/* Out of Stock Dialog */}
        <ConfirmDialog
          isOpen={isOutOfStockDialogOpen}
          onConfirm={handleOutOfStockDialogClose}
          title={t('listing.sold_out_dialog.title')}
          description={t('listing.sold_out_dialog.description')}
          confirmText={t('listing.sold_out_dialog.confirm')}
        />

        {/* Edit Confirmation Dialog */}
        <ConfirmDialog
          isOpen={showEditConfirmDialog}
          onConfirm={handleEditConfirmDialogClose}
          title={pendingEditAction?.message || ''}
          confirmText={t('common.ok')}
        />

        {/* Full Screen Loading during payment verification */}
        <FullScreenLoading
          isVisible={isCheckingPaymentStatus}
          message={t('purchase.payment.verifying_payment')}
          redirectUrl={paymentRedirectUrl}
        />
        <PenaltyRestrictionDialog {...penaltyDialogProps} />
      </div>
    </TooltipProvider>
  );
};

export default PurchasePage; 
